/**
 * Agent releases (agent 0.3.0, migration 0218), on the Plant agents page for platform admins (Sahas staff) only: every
 * published build of the Sahas Forge Agent with its target, size, when it was published, and whether it is urgent or
 * withdrawn; "Mark urgent" / "Not urgent", and "Withdraw" (final, so asked for in a dialog first). Below, the key
 * updates published so far (GET /v1/platform/agent-trust), read-only.
 *
 * One build serves every org, so these routes are not org-scoped and answer 403 to anyone else; the section renders
 * nothing for them and does not ask. Publishing stays on divineiotctl: releases and key updates are signed offline.
 *
 * AgentReleasesView is pure (the tests call it); AgentReleasesPanel holds the gate, the reads and the calls.
 */
import { useState } from "react";
import { Package } from "lucide-react";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Card } from "@/lib/ui/card";
import { ConfirmDialog } from "@/lib/ui/confirm-dialog";
import { useToast } from "@/lib/ui/toast";
import { useOrg } from "@/lib/org-context";
import { errorCodeOf, isNetworkError, statusOf } from "@/lib/api/error";
import {
  type AgentRelease,
  type AgentSignedTrust,
  useAgentReleasesQuery,
  useAgentTrustQuery,
  useSetAgentReleaseUrgent,
  useWithdrawAgentRelease,
} from "@/lib/api/ingest-agents";
import { fmtSize, fmtWhen, sortReleases } from "@/lib/plant-agents";

const TH = "whitespace-nowrap px-4 py-2 text-left font-semibold text-on-surface-muted";

/** A release in a label: "0.3.1 for windows-x64-nsis". */
const releaseName = (r: AgentRelease) => `${r.version} for ${r.target}`;

function releaseErrorWords(err: unknown, fallback: string): string {
  const status = statusOf(err);
  if (status === 403) return "Only a platform admin can change agent releases.";
  if (errorCodeOf(err) === "release_not_found" || status === 404) return "This release no longer exists.";
  if (isNetworkError(err)) return "The platform could not be reached. Check the connection and try again.";
  return fallback;
}

export interface AgentReleasesViewProps {
  releases: AgentRelease[];
  trust: AgentSignedTrust[];
  /** Why the key updates could not be read, or null. */
  trustError?: string | null;
  /** A call is on its way: the actions wait. */
  busy: boolean;
  onUrgent: (r: AgentRelease, urgent: boolean) => void;
  /** Withdraw was pressed: ask first. */
  onAskWithdraw: (r: AgentRelease) => void;
  /** The release whose withdrawal is being asked for, or null. */
  confirmWithdraw: AgentRelease | null;
  onCancelWithdraw: () => void;
  /** Confirmed: withdraw it. */
  onWithdraw: (r: AgentRelease) => void | Promise<void>;
}

export function AgentReleasesView(p: AgentReleasesViewProps) {
  return (
    <section className="mt-3 flex flex-col gap-3 border-t border-border pt-4" aria-labelledby="agent-releases-title">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Package className="size-4 text-on-surface-muted" />
        <h2 id="agent-releases-title" className="whitespace-nowrap text-sm font-semibold text-on-surface">
          Agent releases
        </h2>
        <span className="text-xs text-on-surface-muted">
          Builds of the Sahas Forge Agent offered to every org&apos;s PCs · platform admins only
        </span>
      </div>
      <p className="text-xs text-on-surface-muted">
        Publishing is done with <code className="font-mono text-on-surface">divineiotctl agents releases publish</code>{" "}
        (signing is offline).
      </p>
      {p.releases.length === 0 ? (
        <p className="text-sm text-on-surface-muted">No releases published yet.</p>
      ) : (
        <Card className="overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[40rem] text-sm">
              <thead className="border-b border-border">
                <tr>
                  <th className={TH}>Version</th>
                  <th className={TH}>Target</th>
                  <th className={TH}>Size</th>
                  <th className={TH}>Published</th>
                  <th className={TH}>State</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {sortReleases(p.releases).map((r) => {
                  const withdrawn = r.withdrawn_at !== null;
                  return (
                    <tr
                      key={r.id}
                      data-release={r.id}
                      className={`border-b border-border last:border-b-0 odd:bg-surface-elevated/30 ${withdrawn ? "opacity-60" : ""}`}
                    >
                      <td className="px-4 py-2">
                        <span className="font-mono font-medium text-on-surface" title={`sha256 ${r.sha256}`}>
                          {r.version}
                        </span>
                        {r.channel && r.channel !== "stable" && (
                          <span className="text-xs text-on-surface-muted"> · {r.channel}</span>
                        )}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-on-surface-muted">{r.target}</td>
                      <td className="whitespace-nowrap px-4 py-2 text-xs text-on-surface-muted">{fmtSize(r.size)}</td>
                      <td className="whitespace-nowrap px-4 py-2 text-xs text-on-surface-muted">{fmtWhen(r.created_at)}</td>
                      <td className="px-4 py-2">
                        {withdrawn ? (
                          <>
                            <Badge variant="secondary" className="whitespace-nowrap">
                              Withdrawn
                            </Badge>
                            <div className="mt-0.5 text-xs text-on-surface-muted">on {fmtWhen(r.withdrawn_at)}</div>
                          </>
                        ) : r.urgent ? (
                          <Badge variant="warning" className="whitespace-nowrap">
                            Urgent
                          </Badge>
                        ) : (
                          <Badge variant="success" className="whitespace-nowrap">
                            Offered
                          </Badge>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-2 text-right">
                        {!withdrawn && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={p.busy}
                              aria-label={`${r.urgent ? "Not urgent" : "Mark urgent"}: ${releaseName(r)}`}
                              onClick={() => p.onUrgent(r, !r.urgent)}
                            >
                              {r.urgent ? "Not urgent" : "Mark urgent"}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={p.busy}
                              aria-label={`Withdraw ${releaseName(r)}`}
                              onClick={() => p.onAskWithdraw(r)}
                            >
                              Withdraw
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      <div className="flex flex-col gap-1">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-on-surface-muted">Key updates</h3>
        {p.trustError ? (
          <p className="text-sm text-danger">{p.trustError}</p>
        ) : p.trust.length === 0 ? (
          <p className="text-sm text-on-surface-muted">None published: agents trust the keys their builds pin.</p>
        ) : (
          <ul className="flex flex-col gap-0.5 text-sm text-on-surface">
            {p.trust.map((t) => (
              <li key={t.bundle.seq} data-trust={t.bundle.seq}>
                Key update {t.bundle.seq}
                <span className="text-xs text-on-surface-muted"> · issued {fmtWhen(t.bundle.issued_at)}</span>
                {t.bundle.note && <span className="text-xs text-on-surface-muted"> · {t.bundle.note}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
      <ConfirmDialog
        open={p.confirmWithdraw !== null}
        onClose={p.onCancelWithdraw}
        onConfirm={() => (p.confirmWithdraw ? p.onWithdraw(p.confirmWithdraw) : undefined)}
        title={p.confirmWithdraw ? `Withdraw agent ${releaseName(p.confirmWithdraw)}?` : "Withdraw this release?"}
        description="Agents stop being offered this version, and it is never served again. This is final: to offer an update again, publish a newer version."
        confirmLabel="Withdraw"
        destructive
      />
    </section>
  );
}

/** The section, for platform admins only: nothing (and no read) for anyone else. */
export function AgentReleasesPanel() {
  const { isPlatformAdmin } = useOrg();
  const releases = useAgentReleasesQuery(isPlatformAdmin);
  const trust = useAgentTrustQuery(isPlatformAdmin);
  const withdraw = useWithdrawAgentRelease();
  const urgent = useSetAgentReleaseUrgent();
  const toast = useToast();
  const [confirm, setConfirm] = useState<AgentRelease | null>(null);
  if (!isPlatformAdmin) return null;

  if (releases.isError) {
    return (
      <p className="text-sm text-danger">{releaseErrorWords(releases.error, "Couldn't load the agent releases.")}</p>
    );
  }
  if (!releases.isSuccess) return null;

  const setUrgent = async (r: AgentRelease, on: boolean) => {
    try {
      await urgent.mutateAsync({ id: r.id, urgent: on });
      toast.push(
        "ok",
        on
          ? `${releaseName(r)} is urgent: agents offered it install it at once.`
          : `${releaseName(r)} is no longer urgent.`,
      );
    } catch (err) {
      toast.push("error", releaseErrorWords(err, "Couldn't change the release."));
    }
  };

  const confirmWithdraw = async (r: AgentRelease) => {
    try {
      await withdraw.mutateAsync(r.id);
      toast.push("ok", `${releaseName(r)} is withdrawn: agents are no longer offered it.`);
    } catch (err) {
      toast.push("error", releaseErrorWords(err, "Couldn't withdraw the release."));
    } finally {
      setConfirm(null);
    }
  };

  return (
    <AgentReleasesView
      releases={releases.data.items}
      trust={trust.data?.items ?? []}
      trustError={trust.isError ? releaseErrorWords(trust.error, "Couldn't load the key updates.") : null}
      busy={urgent.isPending || withdraw.isPending}
      onUrgent={(r, on) => void setUrgent(r, on)}
      onAskWithdraw={setConfirm}
      confirmWithdraw={confirm}
      onCancelWithdraw={() => setConfirm(null)}
      onWithdraw={confirmWithdraw}
    />
  );
}
